import { PR_BUILD_STATUS } from "../../utils/constants";
import { badge, badges } from "../../styles/badgeStyles";

// Release-specific tones the handoff's token table does not cover. Light keeps
// today's exact hex so the refactor is a no-op; dark takes the nearest token
// the active palette already defines.
const tone = (c) => ({
  selectedRowBg: c.isDark ? c.selected : "#EFF6FF",
  infoSoftBg: c.isDark ? c.infoBg : "#EFF6FF",
  infoChipBg: c.isDark ? c.infoBg : "#DBEAFE",
  infoDeepFg: c.isDark ? c.infoFg : "#1D4ED8",
  infoEdge: c.isDark ? c.infoFg : "#BFDBFE",
  infoInk: c.isDark ? c.infoFg : "#1E40AF",
  infoIcon: c.isDark ? c.infoFg : "#2563EB",
  infoBtnEdge: c.isDark ? c.infoFg : "#93C5FD",
  neuSoftBg: c.isDark ? c.neuBg : "#EEF0F4",
  neuDeepFg: c.isDark ? c.neuFg : "#4B5563",
  okSoftBg: c.isDark ? c.okBg : "#ECFDF3",
  okChipBg: c.isDark ? c.okBg : "#DCFCE7",
  okDeepFg: c.isDark ? c.okFg : "#15803D",
  warnSoftBg: c.isDark ? c.warnBg : "#FEF0C7",
  warnDeepFg: c.isDark ? c.warnFg : "#B54708",
  amberRowBg: c.isDark ? c.warnBg : "#FFFBEB",
  amberRowTint: c.isDark ? c.warnBg : "#FFFDF7",
  amberRowHover: c.isDark ? c.warnBg : "#FEF9EC",
  amberChipBg: c.isDark ? c.warnBg : "#FEF3C7",
  amberEdge: c.isDark ? c.warnFg : "#FDE68A",
  amberBtnEdge: c.isDark ? c.warnFg : "#FCD34D",
  amberFg: c.isDark ? c.warnFg : "#92400E",
  amberBody: c.isDark ? c.warnText : "#A16207",
  amberIcon: c.isDark ? c.warnText : "#D97706",
  amberSegFg: c.isDark ? c.warnText : "#C2410C",
  purEdge: c.isDark ? c.purFg : "#DDD6FE",
  dangerTint: c.isDark ? c.errBg : "#FEF3F2",
  dangerRowBg: c.isDark ? c.errBg : "#FEF6F5",
  dangerChipBg: c.isDark ? c.errBg : "#FEE2E2",
  dangerEdge: c.isDark ? c.errFg : "#FCD9D6",
  dangerInk: c.isDark ? c.errFg : "#B42318",
  dangerStrong: c.isDark ? c.errFg : "#DC2626",
  subtleBg: c.isDark ? c.surface2 : "#FAFBFC",
  subtleBg2: c.isDark ? c.surface2 : "#FAFAFA",
  pillBg: c.isDark ? c.neuBg : "#F1F2F4",
  trackBg: c.isDark ? c.surface2 : "#F1F3F5",
  dotOk: c.isDark ? c.okText : "#16A34A",
  dotErr: c.isDark ? c.errText : "#D33B3B",
});

export const monoFont =
  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Release view: a flat commit table per patch. Six fixed columns so every patch
// scans against the same rulers; repository is a column, not a nesting level.
export const RELEASE_GRID = "250px 78px 96px 1fr 66px 62px";

// Service view: seven columns — release and patch move in from the group header.
// RELEASE | PATCH | TAG | HASH | MESSAGE | FLAG | PR
export const SERVICE_GRID = "112px 106px 76px 70px 1fr 76px 58px";

export const COMMIT_ROW_HEIGHT = 34;
export const MAX_VISIBLE_ROWS = 12;

export const releaseStyles = (colors) => {
  const t = tone(colors);

  const styles = {
    // Main
    viewSelectForm: {
      minWidth: "150px",
      mt: "5px",
      ml: "2px",
      paddingX: "5px",
    },
    viewSelectSelect: {
      height: "35px",
    },
    viewToggleGroup: {
      height: "32px",
      backgroundColor: colors.divider,
      borderRadius: "6px",
      padding: "2px",
      border: "none",
      "& .MuiToggleButtonGroup-grouped": {
        border: "none",
        borderRadius: "4px !important",
        margin: 0,
      },
    },
    viewToggleButton: {
      px: "14px",
      fontSize: 12,
      fontWeight: 500,
      color: colors.sec,
      border: "none",
      textTransform: "none",
      "&.Mui-selected": {
        backgroundColor: colors.surface,
        color: colors.link,
        fontWeight: 600,
        boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
      },
      "&.Mui-selected:hover": {
        backgroundColor: colors.surface,
      },
    },
    loadingBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "75vh",
    },
  };

  // bg / fg (and optional border) per release and release-label status.
  const STATUS_COLORS = {
    INCUBATING: { bg: t.warnSoftBg, fg: t.warnDeepFg },
    CREATING: { bg: t.infoChipBg, fg: t.infoDeepFg },
    CREATED: { bg: t.neuSoftBg, fg: t.neuDeepFg },
    // Same amber as the patch drawer's double-commit flag (DOUBLE_CHIP_BG /
    // DOUBLE_FG, declared further down this file) — a release sitting outside
    // the normal cycle is worth a look, not an alarm, and reusing the pair keeps
    // "needs attention" one color everywhere rather than inventing a second amber.
    OUT_OF_RELEASE_CYCLE: { bg: t.amberChipBg, fg: t.amberFg },
    ARCHIVED: { bg: colors.divider, fg: colors.sec },
    IN_QA: { bg: t.infoSoftBg, fg: t.infoDeepFg },
    // Same purple as the READY_FOR_STAGE_* label statuses below, without their
    // border: IN_STAGE is the arrived state those transitions lead to, the same
    // relationship IN_QA and IN_PRODUCTION already have to their own siblings.
    IN_STAGE: { bg: colors.purBg, fg: colors.purFg },
    IN_PRODUCTION: { bg: t.okSoftBg, fg: t.okDeepFg },
    READY_FOR_QA_DEPLOYMENT: {
      bg: t.infoSoftBg,
      fg: t.infoDeepFg,
      border: t.infoEdge,
    },
    READY_FOR_STAGE_DEPLOYMENT: {
      bg: colors.purBg,
      fg: colors.purFg,
      border: t.purEdge,
    },
    READY_FOR_STAGE_VALIDATION: {
      bg: colors.purBg,
      fg: colors.purFg,
      border: t.purEdge,
    },
  };

  const statusColors = (status = "") => {
    const value = String(status).toUpperCase();
    if (STATUS_COLORS[value]) return STATUS_COLORS[value];
    if (value.endsWith("_FAILED"))
      return { bg: t.dangerTint, fg: t.dangerInk, border: t.dangerEdge };
    if (value.startsWith("DISCARDED"))
      return { bg: colors.divider, fg: colors.sec };
    return { bg: colors.divider, fg: colors.sec };
  };

  const statusChip = (status) => {
    const { bg, fg, border } = statusColors(status);
    return {
      display: "inline-flex",
      alignItems: "center",
      height: "20px",
      px: "7px",
      borderRadius: "4px",
      fontFamily: monoFont,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      whiteSpace: "nowrap",
      backgroundColor: bg,
      color: fg,
      // Only some statuses carry an explicit border token. The rest fall back to
      // a 25% wash of their own ink, so every chip keeps an edge in its own hue
      // rather than a flat neutral outline.
      border: `1px solid ${border || `${fg}40`}`,
    };
  };

  // Tint of the row whose labels drawer is open.
  const SELECTED_ROW_BG = t.selectedRowBg;

  const BORDER = colors.border;
  const CONTROL_BORDER = colors.ctl;
  const DIVIDER = colors.divider;
  const INK_BODY = colors.body;
  const INK_SECONDARY = colors.sec;
  const BRAND = colors.link;
  const BRAND_HOVER = colors.qaFg;
  const BRAND_TINT = colors.tint;
  const DANGER_INK = t.dangerInk;
  const DANGER_BORDER = t.dangerEdge;
  const DANGER_TINT = t.dangerTint;

  // Shared geometry for the 26px buttons in a release card header.
  const RELEASE_BUTTON = {
    height: "26px",
    minHeight: "26px",
    fontFamily: monoFont,
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    lineHeight: 1.15,
  };

  // Shared geometry for the 28px buttons in a label row.
  const LABEL_BUTTON = {
    height: "24px",
    minHeight: "24px",
    fontFamily: monoFont,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  // Floor for the middle lane, which is the narrowest of the three and holds
  // nowrap text: its longest string, "36 labels, 14 discarded, 16 failed
  // validations", measures 259px at the real font settings, so the lane may not
  // close below that plus a little breathing room.
  const HISTORY_COL = "310px";

  // Floor for the identity lane. cardIdentity's own content (name + age + chips)
  // wraps freely, but labelIdentity's status pill is a single un-splittable
  // nowrap block — its widest real status text ("Being deployed to stage")
  // measures ~145px including padding. flexWrap lets the pill drop to its own
  // line under the timeline+version pair, but the pill itself still can't
  // shrink below that ~145px, so below this floor it would spill into the
  // counts lane beside it.
  const IDENTITY_COL = "160px";

  // identity | counts-or-date | actions, split 40% / 10% / 50%. The card header
  // and every label row beneath it share these tracks — together with the same
  // gap and the same horizontal padding — so the three lanes line up vertically
  // the whole way down a card.
  //
  // fr rather than literal percentages, because fr splits what is left after the
  // gaps; 40%+10%+50% plus two 8px gaps overflows by 16px and the card clips.
  // Note the middle lane only reaches a true 10% on a very wide window: its
  // 259px counts string outgrows 10% below ~2600px of card width, so in practice
  // it rests on the HISTORY_COL floor and the outer two share what is left 4:5.
  const CARD_LANES = `minmax(${IDENTITY_COL}, 4fr) minmax(${HISTORY_COL}, 1fr) minmax(0, 5fr)`;
  const CARD_LANE_GAP = "8px";
  const CARD_LANE_PX = "10px";

  // One card per release, stacked vertically; labels are rows inside the card.
  const summaryTable = {
    pill: (bg, border) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      px: "8px",
      py: "2px",
      borderRadius: "6px",
      backgroundColor: bg,
      border: `1px solid ${border || "transparent"}`,
    }),
    pillStatus: (fg) => ({
      fontSize: 11,
      color: fg,
      whiteSpace: "nowrap",
    }),
    // Lane 2, centred in its own fixed track and always rendered even when empty
    // (the "—" placeholder), so the counts sit on the same centre line on every
    // card no matter how long the string or how many chips the row carries.
    historyStack: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "2px",
    },
    historyLine: {
      textAlign: "center",
      fontSize: 12.5,
      color: "text.secondary",
      whiteSpace: "nowrap",
    },
    // Reads inline with the counts it belongs to, but keeps the failed-red ink.
    historyFailed: {
      color: "error.main",
    },
    menuButton: {
      width: "22px",
      height: "22px",
      fontSize: 16,
      color: "text.disabled",
    },
    menuItem: (destructive) => ({
      fontSize: 12.5,
      color: destructive ? "error.main" : colors.body,
    }),
    // One bordered card per release: tinted header strip, label rows stacked below.
    cardList: {
      mt: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "18px",
      mb: "16px",
    },
    card: (selected) => ({
      border: "1px solid",
      borderColor: selected ? "primary.main" : BORDER,
      borderRadius: "8px",
      backgroundColor: colors.surface,
      overflow: "hidden",
    }),
    // Header row of a card, on the shared CARD_LANES tracks: each lane's position
    // comes from the track sizes alone, so a row's own content can never move the
    // lane beside it. Extra status chips no longer push the counts off their
    // centre, and a longer counts string no longer shifts the action cluster.
    cardHeader: (selected) => ({
      display: "grid",
      gridTemplateColumns: CARD_LANES,
      alignItems: "center",
      gap: CARD_LANE_GAP,
      px: CARD_LANE_PX,
      py: "8px",
      backgroundColor: selected ? SELECTED_ROW_BG : DIVIDER,
      borderBottom: `1px solid ${BORDER}`,
    }),
    // Lane 1: name, age and status chips travel together and wrap inside their
    // own lane instead of breaking the grid. minWidth:0 lets the track shrink
    // past its content rather than blowing the other two lanes out.
    cardIdentity: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px",
      minWidth: 0,
    },
    // The release name is the drawer trigger, so it carries the brand ink. It is
    // a real <button>, which needs its chrome stripped back to plain text.
    cardName: {
      p: 0,
      border: "none",
      backgroundColor: "transparent",
      fontFamily: monoFont,
      fontSize: 15,
      fontWeight: 500,
      color: BRAND,
      cursor: "pointer",
      "&:hover": { textDecoration: "underline" },
      "&:focus-visible": {
        outline: `2px solid ${BRAND}`,
        outlineOffset: "2px",
        borderRadius: "3px",
      },
    },
    cardAge: {
      fontFamily: monoFont,
      fontSize: 12,
      color: INK_SECONDARY,
      whiteSpace: "nowrap",
    },
    // Second line of historyStack, under the label counts — the release's own
    // created date, quieter than the count it sits below.
    cardCreatedDate: {
      fontFamily: monoFont,
      fontSize: 11,
      color: "text.disabled",
      whiteSpace: "nowrap",
    },
    cardChips: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "4px",
    },
    // Lane 3: right-packed inside its own fixed track, so the cluster ends flush
    // with the card edge on every row whatever it contains. It wraps rather than
    // overflows, because the card clips and a button past the edge would vanish.
    cardActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      gap: "8px",
    },
    actionDivider: {
      flexShrink: 0,
      width: "1px",
      height: "16px",
      mx: "2px",
      backgroundColor: BORDER,
    },
    createButton: {
      ...RELEASE_BUTTON,
      px: "8px",
      "& .MuiButton-startIcon": {
        mr: "4px",
        ml: 0,
        "& > *:nth-of-type(1)": { fontSize: 13 },
      },
    },
    patchButton: {
      ...RELEASE_BUTTON,
      px: "8px",
      backgroundColor: colors.surface,
      borderColor: BORDER,
      color: BRAND,
      "&:hover": { backgroundColor: BRAND_TINT, borderColor: BRAND },
      "& .MuiButton-startIcon": {
        mr: "4px",
        ml: 0,
        "& > *:nth-of-type(1)": { fontSize: 13 },
      },
    },
    releaseActionButton: (destructive) => ({
      ...RELEASE_BUTTON,
      px: "9px",
      backgroundColor: colors.surface,
      borderColor: destructive ? DANGER_BORDER : CONTROL_BORDER,
      color: destructive ? DANGER_INK : INK_BODY,
      "&:hover": {
        backgroundColor: destructive ? DANGER_TINT : DIVIDER,
        borderColor: destructive ? DANGER_INK : colors.muted,
      },
    }),
    // Passed to useIamModal so the scoped IAM button matches the header cluster
    // without touching commonButtonStyles, which every other page shares.
    cardIamButton: {
      ...RELEASE_BUTTON,
      // See iamButtonStyles in styles/buttonStyles: kills DataGridButton's default
      // "0 auto" wrapper margin, which would absorb the cluster's free space.
      m: "0px",
      padding: "0 9px",
      backgroundColor: colors.surface,
      borderColor: BORDER,
      "& .MuiTypography-root": {
        fontFamily: monoFont,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: INK_SECONDARY,
      },
      "&:hover": {
        backgroundColor: DIVIDER,
        borderColor: CONTROL_BORDER,
        "& .MuiTypography-root": { color: INK_BODY },
      },
    },

    // One row per active label: timeline dot, version, status, date, actions.
    labelGridRow: {
      display: "grid",
      gridTemplateColumns: CARD_LANES,
      alignItems: "center",
      gap: CARD_LANE_GAP,
      px: CARD_LANE_PX,
      py: "8px",
      borderTop: `1px solid ${DIVIDER}`,
      "&:hover": { backgroundColor: colors.surface2 },
    },
    // Lane 1 of a label row — timeline, version and status pill travel together
    // so the row sits on the header's lanes. Stretches rather than centring, so
    // the timeline rule still spans the full row height and the dots read as one
    // continuous line down the card. flexWrap mirrors cardIdentity: timeline +
    // version + pill have a combined min-content width (~220-280px depending on
    // the status text) that won't shrink below itself, so on a narrow lane it
    // wraps onto a second line inside its own cell instead of overflowing into
    // the counts lane beside it.
    labelIdentity: {
      alignSelf: "stretch",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px",
      minWidth: 0,
    },
    // The rule overshoots the row on both sides so it reads as one continuous
    // line down the card rather than a dash per row. Carries its own 26px now
    // that it sits inside the identity lane rather than in a track of its own.
    timelineCell: {
      position: "relative",
      alignSelf: "stretch",
      flexShrink: 0,
      width: "26px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    timelineRule: {
      position: "absolute",
      top: "-9px",
      bottom: "-9px",
      left: "50%",
      width: "1px",
      marginLeft: "-0.5px",
      backgroundColor: BORDER,
    },
    timelineDot: (fg) => ({
      position: "relative",
      width: "7px",
      height: "7px",
      borderRadius: "50%",
      backgroundColor: fg,
      boxShadow: `0 0 0 3px ${colors.surface}`,
    }),
    // Keeps the 90px the old grid track gave it, so the status pills beside it
    // still start on the same x from one label row to the next.
    labelVersion: {
      flexShrink: 0,
      minWidth: "90px",
      fontFamily: monoFont,
      fontSize: 14,
      fontWeight: 500,
      color: INK_BODY,
      whiteSpace: "nowrap",
    },
    labelDate: {
      textAlign: "center",
      fontFamily: monoFont,
      fontSize: 13,
      color: INK_SECONDARY,
      whiteSpace: "nowrap",
    },
    // Lane 3, right-packed inside its own track so the cluster ends flush with
    // the card edge on every row. A label can permit three actions with long
    // names (the widest set measures 590px), so below a wide header the cluster
    // wraps inside its lane rather than stealing width from the lanes beside it.
    labelActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      flexWrap: "wrap",
      gap: "8px",
    },
    labelForwardButton: {
      ...LABEL_BUTTON,
      px: "10px",
      backgroundColor: BRAND,
      borderColor: BRAND,
      color: colors.surface,
      "&:hover": { backgroundColor: BRAND_HOVER, borderColor: BRAND_HOVER },
    },
    labelActionButton: (destructive) => ({
      ...LABEL_BUTTON,
      px: "10px",
      backgroundColor: colors.surface,
      borderColor: destructive ? DANGER_BORDER : CONTROL_BORDER,
      color: destructive ? DANGER_INK : INK_BODY,
      "&:hover": {
        backgroundColor: destructive ? DANGER_TINT : DIVIDER,
        borderColor: destructive ? DANGER_INK : colors.muted,
      },
    }),
    noActions: {
      fontSize: 13,
      color: INK_SECONDARY,
    },
    emptyLabels: {
      px: "12px",
      py: "11px",
      pl: "46px",
      borderTop: `1px solid ${DIVIDER}`,
      fontSize: 13.5,
      color: INK_SECONDARY,
    },
    showAllRow: {
      px: "12px",
      py: "5px",
      pl: "40px",
      borderTop: `1px solid ${DIVIDER}`,
    },
    showAllButton: {
      height: "26px",
      minHeight: "26px",
      px: "6px",
      fontSize: 12.5,
      color: INK_SECONDARY,
    },

    archivedRow: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      px: "12px",
      py: "8px",
      backgroundColor: t.subtleBg2,
      border: `1px solid ${BORDER}`,
      borderRadius: "8px",
    },
    archivedCount: {
      fontSize: 13,
      fontWeight: 600,
      color: "text.secondary",
    },
    archivedNames: {
      fontFamily: monoFont,
      fontSize: 11,
      color: "text.disabled",
    },
    // Archived releases render as pills so each one can carry its own delete.
    archivedList: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "6px",
      minWidth: 0,
    },
    archivedPill: (canDelete) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "2px",
      pl: "8px",
      pr: canDelete ? "2px" : "8px",
      height: "24px",
      borderRadius: "12px",
      backgroundColor: t.pillBg,
      border: `1px solid ${colors.border}`,
    }),
    archivedPillName: {
      fontFamily: monoFont,
      fontSize: 12,
      color: "text.secondary",
    },
    archivedDelete: {
      p: "2px",
      color: "text.disabled",
      "&:hover": { color: t.dangerInk, backgroundColor: t.dangerChipBg },
    },
  };

  // Group dot tints for the collapsed, terminal-state label groups.
  const GROUP_DOT_COLORS = {
    inProduction: t.dotOk,
    failed: t.dotErr,
    superseded: colors.sec,
    discarded: colors.muted,
  };

  // Right side drawer listing every label of a release: fixed header, scrolling body.
  const labelsDrawer = {
    paper: (width = 560) => ({
      width: { xs: "100%", sm: width },
      maxWidth: "100vw",
      display: "flex",
      flexDirection: "column",
    }),
    header: {
      flexShrink: 0,
      display: "flex",
      alignItems: "flex-start",
      gap: "8px",
      px: "16px",
      py: "14px",
      borderBottom: "1px solid",
      borderColor: "grey.100",
    },
    headerText: {
      flex: 1,
      minWidth: 0,
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px",
    },
    title: {
      fontWeight: 600,
      fontSize: 17,
      color: colors.body,
    },
    subtitle: {
      mt: "4px",
      fontFamily: monoFont,
      fontSize: 12,
      color: "text.disabled",
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    body: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      px: "16px",
      py: "14px",
    },
    sectionHeading: {
      mb: "8px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "text.secondary",
    },
    activeRow: (bg, border) => ({
      display: "grid",
      gridTemplateColumns: { xs: "82px 1fr", sm: "82px 1fr 160px" },
      alignItems: "center",
      gap: "8px",
      px: "12px",
      py: "10px",
      mb: "8px",
      borderRadius: "8px",
      backgroundColor: bg,
      border: `1px solid ${border || "transparent"}`,
    }),
    version: {
      fontFamily: monoFont,
      fontSize: 13,
      fontWeight: 500,
      color: colors.body,
      whiteSpace: "nowrap",
    },
    statusCell: {
      display: "flex",
      alignItems: "baseline",
      flexWrap: "wrap",
      gap: "10px",
      minWidth: 0,
    },
    statusText: (fg) => ({
      fontSize: 12.5,
      color: fg,
      whiteSpace: "nowrap",
    }),
    date: {
      fontFamily: monoFont,
      fontSize: 12,
      color: "text.disabled",
      whiteSpace: "nowrap",
    },
    rowActions: {
      gridColumn: { xs: "1 / -1", sm: "auto" },
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: "2px",
    },
    rowButton: {
      height: "26px",
      minHeight: "26px",
      px: "10px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    },
    // Sets IAM apart from Discard, matching the release card's header cluster.
    actionDivider: {
      flexShrink: 0,
      width: "1px",
      height: "16px",
      mx: "2px",
      backgroundColor: colors.border,
    },
    // The drawer's IAM comes from DataGridButton, not ActionButton, so it needs
    // the outlined skin spelled out — rowButton alone leaves commonButtonStyles'
    // filled blue in place, which is why it used to read as the primary action.
    iamButton: {
      height: "26px",
      minHeight: "26px",
      padding: "0 10px",
      m: "0px",
      backgroundColor: colors.surface,
      borderColor: colors.ctl,
      whiteSpace: "nowrap",
      "& .MuiTypography-root": {
        fontFamily: monoFont,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: colors.sec,
      },
      "&:hover": {
        backgroundColor: colors.divider,
        borderColor: colors.ctl,
        "& .MuiTypography-root": { color: colors.body },
      },
    },
    // Same shape as rowButton, coloured to mark a destructive action (Discard).
    rowButtonDanger: {
      height: "26px",
      minHeight: "26px",
      px: "10px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      color: "error.main",
      borderColor: "error.light",
      "&:hover": {
        color: "error.dark",
        borderColor: "error.main",
        backgroundColor: "error.lighter",
      },
    },
    group: {
      mb: "8px",
      border: "1px solid",
      borderColor: "grey.100",
      borderRadius: "8px",
      backgroundColor: t.subtleBg2,
      "&:before": { display: "none" },
    },
    groupSummary: {
      minHeight: "44px",
      px: "12px",
      flexDirection: "row-reverse",
      gap: "10px",
      "& .MuiAccordionSummary-content": {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        my: 0,
      },
      "& .MuiAccordionSummary-expandIconWrapper": { color: "text.disabled" },
    },
    groupDot: (color) => ({
      width: "9px",
      height: "9px",
      borderRadius: "50%",
      backgroundColor: color,
      flexShrink: 0,
    }),
    groupName: {
      fontSize: 13.5,
      color: colors.body,
    },
    groupCount: {
      fontFamily: monoFont,
      fontSize: 12,
      color: "text.disabled",
    },
    groupRange: {
      ml: "auto",
      fontFamily: monoFont,
      fontSize: 12.5,
      color: "text.disabled",
      whiteSpace: "nowrap",
    },
    groupDetails: {
      px: "12px",
      pt: 0,
      pb: "8px",
    },
    compactRow: {
      display: "grid",
      gridTemplateColumns: { xs: "82px 1fr", sm: "82px 1fr 110px" },
      alignItems: "center",
      gap: "8px",
      py: "7px",
      borderTop: "1px solid",
      borderColor: "grey.100",
    },
    emptyText: {
      mb: "12px",
      fontSize: 12.5,
      color: "text.disabled",
    },
  };

  // Double commits block the patch but nothing has failed, so they read amber; red stays
  // reserved for services whose build actually failed.
  const DOUBLE_BG = t.amberRowBg;
  const DOUBLE_BORDER = t.amberEdge;
  const DOUBLE_FG = t.amberFg;
  // Blue used by the flipped "original work" banner and its Original segment.
  const INFO_FG = t.infoInk;
  const DOUBLE_BODY_FG = t.amberBody;
  const DOUBLE_CHIP_BG = t.amberChipBg;
  const DOUBLE_ICON = t.amberIcon;
  const FAILED_BG = t.dangerRowBg;
  const FAILED_BORDER = t.dangerEdge;
  const FAILED_CHIP_BG = t.dangerChipBg;
  const FAILED_FG = t.dangerInk;

  // The patch banner speaks in two registers. "warning" (amber) is the ordinary
  // case where a few services carry double commits worth unticking. "info" (blue)
  // is the flipped case on an inherited branch: double commits are the norm there,
  // so the banner stops sounding an alarm and instead points at the handful of
  // services that carry original work — the only thing needing a human's eye.
  const BANNER_TONES = {
    warning: {
      bg: DOUBLE_BG,
      border: DOUBLE_BORDER,
      icon: DOUBLE_ICON,
      fg: DOUBLE_FG,
      body: DOUBLE_BODY_FG,
      chipBg: DOUBLE_CHIP_BG,
      btnBorder: t.amberBtnEdge,
    },
    info: {
      bg: t.infoSoftBg,
      border: t.infoEdge,
      icon: t.infoIcon,
      fg: INFO_FG,
      body: t.infoDeepFg,
      chipBg: t.infoChipBg,
      btnBorder: t.infoBtnEdge,
    },
  };

  // Tints of a collapsed service row, keyed by the state it has to carry while closed.
  const SERVICE_TINTS = {
    double: {
      bg: DOUBLE_BG,
      border: DOUBLE_BORDER,
      chipBg: DOUBLE_CHIP_BG,
      chipFg: DOUBLE_FG,
    },
    failed: {
      bg: FAILED_BG,
      border: FAILED_BORDER,
      chipBg: FAILED_CHIP_BG,
      chipFg: FAILED_FG,
    },
    neutral: {
      bg: colors.surface,
      border: colors.border,
      chipBg: t.neuSoftBg,
      chipFg: t.neuDeepFg,
    },
  };

  const tintOf = (state) => SERVICE_TINTS[state] || SERVICE_TINTS.neutral;

  // Right-side drawer that builds one patch: fixed header, banner and filters, scrolling
  // service list, pinned footer.
  const patchDrawer = {
    // `width` is the live px value from useResizableDrawerWidth — desktop only.
    // xs stays a literal 100%; a full-viewport mobile drawer has no edge to drag.
    paper: (width = 720) => ({
      width: { xs: "100%", md: width },
      maxWidth: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }),
    header: {
      flexShrink: 0,
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
      px: "20px",
      py: "16px",
    },
    headerText: {
      flex: 1,
      minWidth: 0,
    },
    titleRow: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px",
    },
    title: {
      fontSize: 18,
      fontWeight: 600,
      color: colors.body,
    },
    readOnlyChip: {
      height: "18px",
      borderRadius: "4px",
      backgroundColor: colors.divider,
      color: "text.secondary",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.06em",
      "& .MuiChip-label": { px: "6px" },
    },
    // "Step 2 of 2" — same slot and geometry as readOnlyChip, but sentence case
    // and not mono, since it names a position rather than a hard state.
    stepChip: {
      height: "18px",
      borderRadius: "4px",
      backgroundColor: colors.divider,
      color: "text.secondary",
      fontSize: 11,
      fontWeight: 500,
      "& .MuiChip-label": { px: "6px" },
    },
    subtitle: {
      mt: "4px",
      fontSize: 12.5,
      color: "text.secondary",
    },
    subtitleVersion: {
      fontFamily: monoFont,
      whiteSpace: "nowrap",
    },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      flexShrink: 0,
    },
    banner: (tone = "warning") => {
      const t = BANNER_TONES[tone] || BANNER_TONES.warning;
      return {
        flexShrink: 0,
        alignItems: "center",
        mx: "20px",
        mb: "12px",
        px: "12px",
        py: "8px",
        borderRadius: "8px",
        backgroundColor: t.bg,
        border: `1px solid ${t.border}`,
        // Icon sits on the title line rather than centred against both lines of copy.
        "& .MuiAlert-icon": {
          py: 0,
          mt: "1px",
          mr: "10px",
          alignSelf: "flex-start",
          color: t.icon,
          fontSize: 20,
        },
        "& .MuiAlert-message": { flex: 1, minWidth: 0, py: 0 },
        "& .MuiAlert-action": { pt: 0, mr: 0, alignItems: "center" },
      };
    },
    bannerTitle: (tone = "warning") => ({
      fontSize: 13.5,
      fontWeight: 500,
      color: (BANNER_TONES[tone] || BANNER_TONES.warning).fg,
    }),
    bannerBody: (tone = "warning") => ({
      fontSize: 12.5,
      color: (BANNER_TONES[tone] || BANNER_TONES.warning).body,
    }),
    bannerButton: (active, tone = "warning") => {
      const t = BANNER_TONES[tone] || BANNER_TONES.warning;
      return {
        height: "28px",
        px: "10px",
        fontFamily: monoFont,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        color: t.fg,
        borderColor: active ? t.fg : t.btnBorder,
        backgroundColor: active ? t.chipBg : colors.surface,
        "&:hover": {
          borderColor: t.fg,
          backgroundColor: active ? t.chipBg : t.bg,
        },
      };
    },
    filterRow: {
      flexShrink: 0,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "4px",
      px: "20px",
      pt: "12px",
      pb: "8px",
    },
    // Second filter row: the two labelled segment clusters, kept on one line side
    // by side; it scrolls horizontally rather than wrapping if the drawer is narrow.
    filterRow2: {
      flexShrink: 0,
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "16px",
      px: "20px",
      pb: "12px",
      overflowX: "auto",
    },
    search: {
      flex: { xs: "1 1 100%", md: "1 1 180px" },
      minWidth: "160px",
      "& .MuiOutlinedInput-root": {
        backgroundColor: colors.surface,
        borderRadius: "8px",
      },
      "& .MuiOutlinedInput-input": { fontSize: 13 },
    },
    searchIcon: {
      mr: "6px",
      fontSize: 16,
      color: "text.disabled",
    },
    segments: {
      height: "34px",
      backgroundColor: colors.divider,
      borderRadius: "8px",
      p: "2px",
      "& .MuiToggleButtonGroup-grouped": {
        border: "none",
        borderRadius: "6px !important",
        margin: 0,
      },
    },
    // A caption + its pill group, kept together so a wrap never splits the label
    // from the buttons it names.
    segmentCluster: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      flexShrink: 0,
    },
    segmentLabel: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: "text.disabled",
      whiteSpace: "nowrap",
    },
    // The caption plus its info affordance; hovering either shows what the group
    // filters by.
    segmentLabelWrap: {
      display: "inline-flex",
      alignItems: "center",
      gap: "3px",
      cursor: "help",
    },
    segmentLabelIcon: {
      fontSize: 13,
      color: "text.disabled",
    },
    // Roomier tooltip for the two-part explanation.
    segmentTooltip: {
      maxWidth: 260,
      fontSize: 12,
      lineHeight: 1.5,
    },
    segment: (color) => ({
      px: "12px",
      fontSize: 12,
      fontWeight: 500,
      textTransform: "none",
      whiteSpace: "nowrap",
      color: color || colors.sec,
      "&.Mui-selected": {
        backgroundColor: colors.surface,
        color: color || colors.link,
        fontWeight: 600,
        boxShadow: "0 1px 3px rgba(0,0,0,0.10)",
      },
      "&.Mui-selected:hover": { backgroundColor: colors.surface },
    }),
    expandButton: {
      height: "34px",
      ml: "auto",
      px: "12px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      color: "text.secondary",
      borderColor: colors.border,
    },
    // Sits immediately left of Expand all; it carries the auto margin so the two
    // stay grouped at the right end of the filter row.
    // Spacing between the row's buttons comes from filterRow's gap alone; an
    // extra margin here would stack on top of it.
    selectAllButton: {
      height: "34px",
      ml: "auto",
      px: "12px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      color: "text.secondary",
      borderColor: colors.border,
    },
    list: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      px: "20px",
      pb: "16px",
    },
    // Only the header band carries the tint; the expanded commit rows stay white so the
    // message text keeps full contrast.
    service: (state) => ({
      mb: "8px",
      borderRadius: "8px",
      boxShadow: "none",
      backgroundColor: colors.surface,
      border: `1px solid ${tintOf(state).border}`,
      "&:before": { display: "none" },
      "&.Mui-expanded": { margin: "0 0 8px" },
    }),
    serviceSummary: (state) => ({
      minHeight: "44px",
      px: "12px",
      flexDirection: "row-reverse",
      gap: "10px",
      borderRadius: "7px",
      backgroundColor: tintOf(state).bg,
      "&.Mui-expanded": { minHeight: "44px", borderRadius: "7px 7px 0 0" },
      "& .MuiAccordionSummary-content": {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        minWidth: 0,
        my: 0,
      },
      "& .MuiAccordionSummary-expandIconWrapper": { color: "text.disabled" },
    }),
    serviceName: {
      fontSize: 13.5,
      fontWeight: 500,
      color: colors.body,
    },
    countChip: (state) => ({
      height: "18px",
      borderRadius: "4px",
      backgroundColor: tintOf(state).chipBg,
      color: tintOf(state).chipFg,
      fontSize: 11,
      "& .MuiChip-label": { px: "6px" },
    }),
    // One collapsed-row chip, coloured by the facet it names. The composition and
    // build-status facets each map to a tone so a mixed row is legible at a glance.
    facetChip: (tone) => {
      const palette = {
        double: { bg: DOUBLE_CHIP_BG, fg: DOUBLE_FG },
        neutral: { bg: t.neuSoftBg, fg: t.neuDeepFg },
        failed: { bg: FAILED_CHIP_BG, fg: FAILED_FG },
        pending: { bg: DOUBLE_CHIP_BG, fg: DOUBLE_FG },
        // Review step's "n left out" service-header chip — a step down in
        // contrast from `neutral`, since it names an absence, not a fact.
        leftOut: { bg: colors.divider, fg: colors.sec },
      };
      const c = palette[tone] || palette.neutral;
      return {
        height: "18px",
        borderRadius: "4px",
        backgroundColor: c.bg,
        color: c.fg,
        fontSize: 11,
        "& .MuiChip-label": { px: "6px" },
      };
    },
    // Holds the facet chips so they stay grouped and wrap as a block if the row
    // ever runs short on width, rather than shoving the version off-screen.
    facetChips: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "6px",
      minWidth: 0,
    },
    serviceVersion: {
      ml: "auto",
      fontFamily: monoFont,
      fontSize: 12,
      color: "text.disabled",
      whiteSpace: "nowrap",
    },
    serviceDetails: {
      px: "12px",
      pt: 0,
      pb: "8px",
      backgroundColor: colors.surface,
      borderRadius: "0 0 7px 7px",
    },
    commitRow: {
      display: "grid",
      // Fixed so the version column stays aligned down the list. Long tags
      // ("0.2.43-rel-0.143.0") ellipsise rather than overflowing onto the
      // message, with the full tag in a tooltip.
      gridTemplateColumns: "28px 88px 1fr 104px",
      alignItems: "center",
      gap: "12px",
      py: "8px",
      borderTop: "1px solid",
      borderColor: "grey.100",
    },
    commitCheckbox: {
      p: 0,
      justifySelf: "center",
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    commitVersion: {
      fontFamily: monoFont,
      fontSize: 12,
      color: colors.body,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
    },
    // One blocking repo per line; pre-line keeps the \n separators.
    blockedListTooltip: {
      maxWidth: 360,
      "& .MuiTypography-root": { whiteSpace: "pre-line" },
    },
    // "N more" is the hover target for the repos the message could not name.
    moreBlocked: {
      textDecoration: "underline dotted",
      textUnderlineOffset: "2px",
      cursor: "help",
    },
    commitMessage: {
      color: colors.body,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      // RedirectLink renders the message as an h5 Link; override it here so the
      // commit text reads as body copy, not a heading.
      "& a": {
        fontSize: 12.5,
        fontWeight: 400,
        lineHeight: 1.4,
      },
    },
    // Per-commit marker: names the one double commit on its own row, so an
    // expanded service reads which commits are carried over and which is native
    // to this branch — the collapsed count chip only totals them.
    commitDoubleTag: {
      flexShrink: 0,
      ...badge(DOUBLE_CHIP_BG, DOUBLE_FG),
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
    },
    commitStatus: (buildStatus) => {
      const tintMap = {
        [PR_BUILD_STATUS[0]]: badge(DOUBLE_CHIP_BG, DOUBLE_FG),
        [PR_BUILD_STATUS[1]]: badge(t.okChipBg, t.okDeepFg),
        [PR_BUILD_STATUS[2]]: badge(t.dangerChipBg, t.dangerStrong),
      };
      return {
        ...(tintMap?.[buildStatus] || badges.neutral),
        justifySelf: "end",
        fontFamily: monoFont,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.04em",
      };
    },
    noCommitsLine: {
      py: "12px",
      fontSize: 12.5,
      textAlign: "center",
      color: "text.disabled",
    },
    emptyBody: {
      flex: 1,
      px: "20px",
      py: "16px",
      fontSize: 13,
      color: "text.secondary",
    },
    loadingBody: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      py: "16px",
      fontSize: 13,
      color: "text.secondary",
    },

    // Review step (step 2): read-only recap shown between "Create patch" and the
    // actual POST. Reuses service/serviceName/facetChip/serviceDetails/commitRow's
    // sibling styles below rather than duplicating them.
    reviewBody: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      px: "20px",
      pb: "16px",
      backgroundColor: colors.drawerBody,
    },
    summaryCard: {
      border: `1px solid ${colors.border}`,
      borderRadius: "8px",
      backgroundColor: colors.surface,
      px: "14px",
      pt: "12px",
      pb: "6px",
      mb: "12px",
    },
    summaryHeadingRow: {
      display: "flex",
      alignItems: "baseline",
      flexWrap: "wrap",
      gap: "8px",
      pb: "6px",
    },
    summaryHeading: {
      fontSize: 15,
      fontWeight: 600,
      color: colors.ink,
    },
    summarySubcopy: {
      fontSize: 12.5,
      color: "text.secondary",
    },
    summaryRow: {
      display: "flex",
      gap: "12px",
      py: "7px",
      borderTop: `1px solid ${colors.divider}`,
    },
    summaryKey: {
      flex: "none",
      width: "150px",
      fontSize: 12.5,
      color: "text.secondary",
    },
    summaryValue: {
      flex: 1,
      minWidth: 0,
      fontSize: 12.5,
      color: colors.body,
    },
    // Composed onto summaryValue's children for the mono/strong/double spans a
    // row needs — e.g. the label tag, or the "N double commits" count.
    summaryValueMono: {
      fontFamily: monoFont,
      fontWeight: 500,
      color: colors.ink,
    },
    summaryStrong: {
      fontWeight: 600,
      color: colors.ink,
    },
    summaryDouble: {
      color: DOUBLE_FG,
    },
    sectionDivider: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      mb: "8px",
    },
    sectionRule: {
      flex: 1,
      height: "1px",
      backgroundColor: colors.border,
    },
    hideLeftOutButton: {
      height: "24px",
      px: "8px",
      fontFamily: monoFont,
      fontSize: 9.5,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      color: "text.secondary",
      borderColor: colors.border,
      "&:hover": { backgroundColor: colors.divider, color: colors.body },
    },
    // Flat (non-accordion) stand-in for serviceSummary: same tint/geometry, but
    // a plain row rather than row-reverse, since there is no expand icon to make
    // room for once the disclosure is gone.
    reviewServiceHeader: (state) => ({
      minHeight: "44px",
      px: "12px",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
      borderRadius: "7px 7px 0 0",
      backgroundColor: tintOf(state).bg,
    }),
    // serviceVersion reads as text.disabled, which the review step avoids for
    // contrast (see styles.js header note) — same geometry, darker ink.
    reviewServiceVersion: {
      ml: "auto",
      fontFamily: monoFont,
      fontSize: 12,
      color: "text.secondary",
      whiteSpace: "nowrap",
    },
    // commitRow without its 28px checkbox column.
    commitRowReadOnly: {
      display: "grid",
      gridTemplateColumns: "88px minmax(0,1fr) 104px",
      alignItems: "start",
      gap: "12px",
      py: "8px",
      borderTop: "1px solid",
      borderColor: "grey.100",
    },
    commitVersionLeftOut: {
      color: "text.secondary",
      textDecoration: "line-through",
    },
    commitMessageLeftOut: {
      color: "text.secondary",
      "& a": { color: "inherit", textDecoration: "line-through" },
    },
    leftOutTag: {
      ...badges.neutral,
      justifySelf: "end",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
    },

    footer: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      px: "20px",
      py: "12px",
      borderTop: `1px solid ${colors.border}`,
      backgroundColor: t.subtleBg,
    },
    footerStatus: (blocked) => ({
      minWidth: 0,
      fontSize: 12.5,
      color: blocked ? DOUBLE_FG : "text.secondary",
    }),
    footerActions: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexShrink: 0,
    },
    cancelButton: {
      height: "34px",
      px: "14px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: "text.secondary",
      borderColor: colors.border,
    },
    confirmButton: {
      height: "34px",
      px: "14px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      "&.Mui-disabled": {
        backgroundColor: colors.divider,
        border: `1px solid ${colors.border}`,
        color: colors.muted,
      },
    },
  };

  // Release view: a flat commit table per patch. Six fixed columns so every patch scans
  // against the same rulers; repository is a column, not a nesting level.

  // Service view: seven columns — release and patch move in from the group header.
  // RELEASE | PATCH | TAG | HASH | MESSAGE | FLAG | PR

  const NODC_CHIP = { bg: t.neuSoftBg, fg: t.neuDeepFg };
  const DOUBLE_SEGMENT_FG = t.amberSegFg;
  const DOUBLE_ROW_TINT = t.amberRowTint;

  const releaseView = {
    container: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      pt: "12px",
    },

    // Filter bar: one row of controls, all 34px tall, with the segment pill and
    // the expand/collapse pair held together on the right.
    // Sticky so it stays visible while the commit groups scroll beneath it.
    // top uses the CSS var written by the sticky header ref in index.jsx so the
    // filter bar always docks flush below the header regardless of font rendering.
    filterBar: {
      position: "sticky",
      top: "var(--releases-header-height, 80px)",
      zIndex: 9,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "12px",
      p: "8px 10px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    },
    filterGroup: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "8px",
    },
    filterActions: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      ml: "auto",
    },
    // Triggers hug their own label; only the search field absorbs slack.
    filterButton: {
      height: "34px",
      px: "10px",
      borderRadius: "8px",
    },
    searchField: {
      width: "300px",
      "& .MuiInputBase-root": {
        height: "34px",
        fontSize: 12.5,
        borderRadius: "8px",
      },
    },
    // Applied-filters row below the bar; hidden when nothing is selected.
    appliedFiltersRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "6px",
    },
    appliedFiltersLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      color: "text.secondary",
    },
    appliedFilterChip: {
      height: "22px",
      borderRadius: "4px",
      bgcolor: colors.tint,
      color: colors.qaFg,
      fontFamily: monoFont,
      fontSize: 11,
      fontWeight: 600,
      maxWidth: "none",
      "& .MuiChip-label": { overflow: "visible", textOverflow: "clip" },
      "& .MuiChip-deleteIcon": {
        fontSize: 14,
        color: colors.link,
        "&:hover": { color: colors.qaFg },
      },
    },
    clearAllButton: {
      ml: "auto",
      textTransform: "none",
      fontSize: 12,
      fontWeight: 600,
      minWidth: 0,
      p: "2px 8px",
    },
    // Segmented pill: a grey track with the active segment lifted out in white.
    segmentGroup: {
      height: "34px",
      p: "3px",
      borderRadius: "8px",
      backgroundColor: t.trackBg,
      "& .MuiToggleButtonGroup-grouped": {
        border: "none",
        borderRadius: "6px !important",
        mx: 0,
      },
    },
    // Double reads amber, doubtful reads amber-muted, no-ticket reads muted.
    segmentButton: (tone) => ({
      px: "12px",
      fontSize: 12.5,
      fontWeight: 500,
      textTransform: "none",
      whiteSpace: "nowrap",
      color:
        { double: DOUBLE_SEGMENT_FG, doubtful: t.amberFg, muted: colors.muted }[
          tone
        ] || colors.ink,
      "&:hover": { backgroundColor: "rgba(255,255,255,0.6)" },
      "&.Mui-selected": {
        backgroundColor: colors.surface,
        boxShadow: "0 1px 2px rgba(16,24,40,0.1)",
        color:
          { double: DOUBLE_SEGMENT_FG, doubtful: t.amberFg, muted: colors.sec }[
            tone
          ] || colors.ink,
        "&:hover": { backgroundColor: colors.surface },
      },
    }),
    // Info affordance sitting just left of the segmented pill; hovering shows what
    // the group filters by.
    segmentLabelWrap: {
      display: "inline-flex",
      alignItems: "center",
      cursor: "help",
    },
    segmentInfoIcon: {
      fontSize: 13,
      color: "text.disabled",
      "&:hover": { color: colors.sec },
    },
    segmentTooltip: {
      maxWidth: 300,
      "& b": { fontWeight: 700 },
    },
    segmentTooltipRow: {
      display: "block",
      fontSize: 12,
      lineHeight: 1.5,
      "& + &": { mt: "6px" },
    },
    // Same outlined toggle the create-patch drawer uses.
    expandButton: {
      height: "34px",
      px: "12px",
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      color: "text.secondary",
      borderColor: colors.border,
    },

    // Patch block
    patchBlock: {
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      overflow: "hidden",
    },
    patchHeader: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      width: "100%",
      px: "10px",
      py: "8px",
      border: "none",
      font: "inherit",
      cursor: "pointer",
      textAlign: "left",
      backgroundColor: colors.surface,
      "&:hover": { backgroundColor: t.subtleBg },
    },
    patchChevron: (open) => ({
      fontSize: 18,
      color: "text.disabled",
      transform: open ? "rotate(90deg)" : "none",
      transition: "transform 120ms",
    }),
    patchLabel: {
      fontFamily: monoFont,
      fontSize: 13,
      fontWeight: 600,
      color: colors.ink,
    },
    patchCount: (amber) => ({
      ...badge(
        amber ? DOUBLE_CHIP_BG : colors.divider,
        amber ? DOUBLE_FG : t.neuDeepFg
      ),
      fontSize: 10.5,
    }),
    patchRange: {
      ml: "auto",
      fontFamily: monoFont,
      fontSize: 11,
      color: "text.disabled",
      whiteSpace: "nowrap",
    },

    // Commit table
    headerRow: {
      display: "grid",
      gridTemplateColumns: RELEASE_GRID,
      alignItems: "center",
      gap: "10px",
      px: "10px",
      height: "26px",
      backgroundColor: "grey.100",
      borderTop: `1px solid ${colors.border}`,
      borderBottom: `1px solid ${colors.border}`,
    },
    headerCell: {
      fontFamily: monoFont,
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: colors.sec,
    },
    row: (isDouble) => ({
      display: "grid",
      gridTemplateColumns: RELEASE_GRID,
      alignItems: "center",
      gap: "10px",
      px: "10px",
      boxSizing: "border-box",
      height: `${COMMIT_ROW_HEIGHT}px`,
      borderBottom: `1px solid ${colors.divider}`,
      backgroundColor: isDouble ? DOUBLE_ROW_TINT : colors.surface,
      "&:hover": { backgroundColor: isDouble ? t.amberRowHover : t.subtleBg },
      "&:hover .commit-hash": { opacity: 1 },
    }),
    repoCell: {
      fontSize: 12,
      color: colors.body,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    tagCell: {
      fontFamily: monoFont,
      fontSize: 11,
      color: t.neuDeepFg,
      whiteSpace: "nowrap",
    },
    ticketLink: {
      fontFamily: monoFont,
      fontSize: 11,
      color: colors.link,
      textDecoration: "none",
      whiteSpace: "nowrap",
      "&:hover": { textDecoration: "underline" },
    },
    ticketEmpty: {
      fontFamily: monoFont,
      fontSize: 11,
      color: "text.disabled",
    },
    messageCell: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: 0,
    },
    messageText: {
      flex: 1,
      minWidth: 0,
      fontSize: 12,
      color: colors.body,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
    hashBox: {
      display: "flex",
      alignItems: "center",
      gap: "2px",
      flexShrink: 0,
      opacity: 0,
      transition: "opacity 120ms",
    },
    hashText: {
      fontFamily: monoFont,
      fontSize: 10.5,
      color: "text.disabled",
      whiteSpace: "nowrap",
    },
    copyButton: {
      width: "18px",
      height: "18px",
      color: "text.disabled",
    },
    flagChip: (nodc) => ({
      ...badge(
        nodc ? NODC_CHIP.bg : DOUBLE_CHIP_BG,
        nodc ? NODC_CHIP.fg : DOUBLE_FG
      ),
      fontFamily: monoFont,
      fontSize: 9.5,
      fontWeight: 600,
      letterSpacing: "0.04em",
      padding: "1px 5px",
    }),
    // Service view — 7-column grid
    serviceRow: (flagKind) => ({
      display: "grid",
      gridTemplateColumns: SERVICE_GRID,
      alignItems: "center",
      gap: "8px",
      px: "10px",
      boxSizing: "border-box",
      height: `${COMMIT_ROW_HEIGHT}px`,
      borderBottom: `1px solid ${colors.divider}`,
      backgroundColor: flagKind === "double" ? DOUBLE_ROW_TINT : colors.surface,
      "&:hover": {
        backgroundColor: flagKind === "double" ? t.amberRowHover : t.subtleBg,
      },
      "&:hover .commit-hash": { opacity: 1 },
    }),
    serviceHeaderRow: {
      display: "grid",
      gridTemplateColumns: SERVICE_GRID,
      alignItems: "center",
      gap: "8px",
      px: "10px",
      height: "26px",
      backgroundColor: "grey.100",
      borderTop: `1px solid ${colors.border}`,
      borderBottom: `1px solid ${colors.border}`,
    },
    // Patch cell is highlighted when patch label differs from release label.
    patchCellHighlight: {
      fontFamily: monoFont,
      fontSize: 11,
      color: colors.link,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontWeight: 600,
    },
    releaseCellMono: {
      fontFamily: monoFont,
      fontSize: 11,
      color: t.neuDeepFg,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    prCell: {
      fontFamily: monoFont,
      fontSize: 11,
      color: colors.sec,
      textDecoration: "none",
      justifySelf: "end",
      whiteSpace: "nowrap",
      "&:hover": { textDecoration: "underline" },
    },
    tableFooter: {
      px: "10px",
      py: "6px",
      fontSize: 11.5,
      color: "text.disabled",
      backgroundColor: t.subtleBg,
    },

    // Empty state
    emptyBanner: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      p: "14px 16px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      backgroundColor: t.subtleBg,
    },
    emptyTitle: {
      fontSize: 13.5,
      fontWeight: 600,
      color: colors.body,
    },
    emptyReason: {
      fontSize: 12.5,
      color: "text.secondary",
    },
    emptyButton: {
      ml: "auto",
      flexShrink: 0,
      height: "30px",
      px: "12px",
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      borderColor: colors.border,
      color: "text.secondary",
    },
    emptyRelease: {
      display: "flex",
      alignItems: "baseline",
      flexWrap: "wrap",
      gap: "6px",
      p: "10px 12px",
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
    },
    emptyReleaseLabel: {
      mr: "4px",
      fontFamily: monoFont,
      fontSize: 12,
      fontWeight: 600,
      color: colors.ink,
    },
    emptyChip: {
      height: "18px",
      fontFamily: monoFont,
      fontSize: 10,
      backgroundColor: colors.divider,
      color: colors.sec,
    },
    emptyMore: {
      fontSize: 11,
      color: "text.disabled",
    },
  };

  return {
    styles,
    statusColors,
    statusChip,
    summaryTable,
    labelsDrawer,
    patchDrawer,
    releaseView,
    GROUP_DOT_COLORS,
    DOUBLE_FG,
    INFO_FG,
  };
};
